'use client';

import { useId, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiLinkM, RiSparkling2Line } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ChangelogEntrySummary, ChangelogKind, ChangelogKindCount } from '@mentorivo/shared';

import { changelogAnchor } from '@/lib/changelog-api';
import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Sürüm çizelgesi — sayfanın kendisi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden KART IZGARASI değil ÇİZELGE
 * ═══════════════════════════════════════════════════════════════════════════
 * Blog dizini üç sütunlu bir ızgara ve orada doğru: yazılar birbirinden
 * bağımsız, okuma sırası yok, göz hangisine isterse gidiyor.
 *
 * Sürüm kayıtları öyle değil — aralarındaki tek ilişki SIRA. Izgaraya
 * dizilselerdi "önce ne oldu" sorusu kaybolurdu: iki sütunlu bir düzende
 * gözün soldan sağa mı yukarıdan aşağı mı okuyacağı belirsiz ve kronoloji
 * tam da bu belirsizlikte ölüyor. Tek sütunlu çizelge, sayfanın taşıdığı tek
 * bilgiyi — zamanın yönünü — düzenin kendisine yazıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Hareket: hat aşağı doğru ÇİZİLİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Her kaydın solundaki dikey hat parçası, kayıt görünür alana girdiğinde
 * yukarıdan aşağı uzuyor (`scaleY`, `transform-origin: top`). Parçalar bitişik
 * olduğu için kaydırdıkça tek bir hat çiziliyormuş gibi duruyor.
 *
 * Bu, DESIGN.md §4'ün istediği türden bir hareket: süs değil, bir şey
 * açıklıyor — "aşağı gidiyorsun, geçmişe gidiyorsun". Kartların tek tek
 * belirmesi bunu söylemiyordu; hattın kendisinin ilerlemesi söylüyor.
 *
 * `whileInView` + `once: true` (DESIGN.md şartı): hat bir kez çizildi mi
 * çizilmiş kalıyor. Her kaydırmada yeniden çizilseydi sayfa huzursuz olurdu
 * ve kullanıcı yukarı dönünce geçmişi "silinmiş" bulurdu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Süzgeç neden sekme (tab) değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Blog listesindekiyle aynı gerekçe ve aynı çözüm: görüntü sekmeye benziyor
 * ama altındaki şey bir "panel" değil, süzülmüş tek bir liste. Düğmeler
 * `aria-pressed` taşıyor ve sonucun kaç kayıt olduğu `aria-live` ile
 * duyuruluyor.
 *
 * Süzgeç İSTEMCİDE: kayıtların hepsi zaten geldi (`lib/changelog-api.ts`).
 *
 * ⚠️ Süzgeç kaydın TAMAMINI gösteriyor, satırlarını süzmüyor. "Düzeltmeler"e
 * basan kişi, düzeltme içeren kayıtları görüyor — o kayıtların yeni özellik
 * satırları da yerinde duruyor. Satırları süzmek daha "doğru" görünürdü ve
 * yanlış olurdu: yarısı silinmiş bir sürüm notu, o sürümde ne olduğunu
 * yanlış anlatır.
 */

/** Süzgeç düğmeleri: "tümü" + sözleşmedeki dört tür. */
type Filter = 'all' | ChangelogKind;

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headItemV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Kaydın içi: künye → başlık → özet → kapak → satırlar. Sıkı stagger
   (`tight`), çünkü bir kayıtta 6-10 öğe var ve `base` ile toplam süre bir
   saniyeyi aşıyordu (lib/motion.ts'teki kural: eleman × değer > 1.2 sn ise
   bir tık düşür). */
const entryV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

/* Blur YOK: ekranda aynı anda birkaç kayıt açılıyor ve her kayıt 6-10 öğe —
   §6'nın ~15 öğelik blur bütçesi tek kaydırmada tükenirdi. */
const lineV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/* Hat parçası yukarıdan aşağı uzuyor — zamanın yönü. Yay DEĞİL tween:
   bir yayın sonundaki hafif sıçrama, bitişik parçaların birleştiği noktada
   görünür bir zıplama üretirdi. */
const railV: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: { duration: dur.base, ease: ease.out } },
};

/* Nokta hat başladıktan sonra oturuyor. `bouncy` yalnız burada: çizelgedeki
   tek "olay" işareti ve hafif bir oturma onu hattın sıradan bir parçası
   olmaktan çıkarıyor. */
const dotV: Variants = {
  hidden: { opacity: 0, scale: 0.3 },
  show: { opacity: 1, scale: 1, transition: { ...springFade(spring.bouncy), delay: 0.08 } },
};

/* Kapak görseli ölçekten açılıyor — blog kartındaki `.posts-cover` ile aynı
   jest ve aynı 0.98 başlangıcı. */
const shotV: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default) },
};

export function ChangelogList({
  entries,
  kinds,
}: {
  entries: ChangelogEntrySummary[];
  kinds: ChangelogKindCount[];
}) {
  const t = useTranslations('changelog');
  const locale = useLocale();
  const uid = useId();

  const [filter, setFilter] = useState<Filter>('all');

  const shown = useMemo(
    () =>
      filter === 'all'
        ? entries
        : entries.filter((entry) => entry.items.some((item) => item.kind === filter)),
    [entries, filter],
  );

  /* Sayaçlar sunucudan geliyor ve süzgeç UYGULANMADAN hesaplanmış
     (`ChangelogKindCount` sözleşme notu). Sıfır sayılı tür düğme olarak
     basılmıyor: "Kaldırılan (0)" yazan bir düğme, basıldığında boş liste
     veren bir düğmedir. */
  const chips = useMemo(() => kinds.filter((kind) => kind.count > 0), [kinds]);

  return (
    <section className="chlog" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-2fr" />

        <div className="grid-item-8fr">
          <div className="chlog-frame">
            <motion.div className="chlog-head" variants={headV} initial={false} animate="show">
              <motion.h2 className="chlog-title" id={`${uid}-title`} variants={headItemV}>
                {t('list.title')}
              </motion.h2>

              {/* Yatay kaydırma KABIN kendisinde: beş süzgeç 390px'te tek
                  satıra sığmıyor ve sayfa gövdesi hiçbir genişlikte yatay
                  kaymamalı (DESIGN.md §2). */}
              <motion.div
                className="chlog-filter"
                role="group"
                aria-label={t('list.filterLabel')}
                variants={headItemV}
              >
                {(['all', ...chips.map((chip) => chip.key)] as Filter[]).map((key) => {
                  const active = key === filter;
                  const count =
                    key === 'all'
                      ? entries.length
                      : (chips.find((chip) => chip.key === key)?.count ?? 0);

                  return (
                    <button
                      key={key}
                      type="button"
                      className="chlog-chip"
                      data-kind={key}
                      aria-pressed={active}
                      onClick={() => setFilter(key)}
                    >
                      {/* Etkin süzgecin altındaki koyu hap `layoutId` ile
                          TAŞINIYOR: yeni bir hap belirip eskisi kaybolsaydı
                          "iki ayrı şey" olurdu; kayan tek hap "aynı seçim
                          şimdi burada" diyor. */}
                      {active ? (
                        <motion.span
                          className="chlog-chip-mark"
                          aria-hidden="true"
                          layoutId={`${uid}-chip`}
                          transition={spring.default}
                        />
                      ) : null}
                      <span className="chlog-chip-text">
                        {key === 'all' ? t('list.all') : t(`kinds.${key}`)}
                        <span className="chlog-chip-count">{count}</span>
                      </span>
                    </button>
                  );
                })}
              </motion.div>

              <motion.p className="chlog-count" aria-live="polite" variants={headItemV}>
                {t('list.count', { count: shown.length })}
              </motion.p>
            </motion.div>

            {shown.length === 0 ? (
              <p className="chlog-empty">
                {entries.length === 0 ? t('list.emptyAll') : t('list.emptyFilter')}
              </p>
            ) : (
              /* `key={filter}` sahneyi yeniden kuruyor — blog ızgarasıyla aynı
                 karar: süzülmüş liste "yeni bir liste" ve dalganın kendisi
                 bunu söylüyor. `AnimatePresence` + `layout` ile öğeleri
                 yerinde kaydırmak denenmedi bilinçli: bu depoda `popLayout`
                 özel bileşenlerde sessizce çalışmıyor (uçan öğe akışta kalıp
                 kabı iki katına çıkarıyor). */
              <ol className="chlog-list" key={filter}>
                {shown.map((entry) => (
                  <Entry key={entry.slug} entry={entry} locale={locale} />
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="grid-item-2fr" />
      </div>
    </section>
  );
}

/**
 * Tek kayıt.
 *
 * Ayrı bileşen çünkü her kaydın KENDİ `whileInView` tetiği var: liste
 * düzeyinde tek bir tetik olsaydı, ekranın dışındaki otuz kayıt da ilk
 * kaydırmada birden açılırdı — ve çizelgenin "ilerledikçe çiziliyor" fikri
 * ilk ekranda tükenirdi.
 */
function Entry({ entry, locale }: { entry: ChangelogEntrySummary; locale: string }) {
  const t = useTranslations('changelog');

  return (
    <motion.li
      className="chlog-entry"
      /* Çıpa BURADA: `/changelog#<slug>` doğrudan bu kaydı açıyor. Kartta
         değil `li`de, çünkü tarayıcı çıpaya kaydırırken künyeyi de (tarih,
         sürüm) ekranda tutmalı. */
      id={entry.slug}
      data-highlight={entry.highlight}
      variants={entryV}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
    >
      {/* Hat ve nokta — çizelgenin kendisi. İkisi de `aria-hidden`: sıra
          bilgisi listenin `<ol>` olmasında zaten var. */}
      <motion.span className="chlog-rail" aria-hidden="true" variants={railV} />
      <motion.span className="chlog-dot" aria-hidden="true" variants={dotV} />

      <div className="chlog-when">
        <motion.time className="chlog-date" dateTime={entry.releasedAt} variants={lineV}>
          {formatDate(entry.releasedAt, locale)}
        </motion.time>

        {/* Sürüm rozeti YALNIZ varsa. Sözleşme `version`u `null`
            bırakabiliyor ve gerekçesi şemada: her duyurunun sürüm numarası
            yok, uydurulmuş bir numara tarihten kötü. */}
        {entry.version ? (
          <motion.span className="chlog-version" variants={lineV}>
            {entry.version}
          </motion.span>
        ) : null}

        {entry.highlight ? (
          <motion.span className="chlog-star" variants={lineV}>
            <RiSparkling2Line size={14} aria-hidden="true" />
            {t('list.highlight')}
          </motion.span>
        ) : null}
      </div>

      <article className="chlog-card">
        <motion.h3 className="chlog-name" variants={lineV}>
          {entry.title}
          {/* Kalıcı bağlantı. Düğme değil `<a>`: adres çubuğunu değiştirmesi
              ve "bağlantıyı kopyala" menüsünün çalışması gereken şey tam
              olarak bir bağlantı. Ad başlığı da içeriyor — ekran okuyucuda
              "bağlantı" demek, hangi kaydın bağlantısı olduğunu söylemiyor. */}
          <a
            className="chlog-anchor"
            href={changelogAnchor(entry.slug)}
            aria-label={t('list.permalink', { title: entry.title })}
          >
            <RiLinkM size={16} aria-hidden="true" />
          </a>
        </motion.h3>

        <motion.p className="chlog-text" variants={lineV}>
          {entry.summary}
        </motion.p>

        {/* Kapak varsa. `alt=""` çünkü görsel kartın İÇİNDE ve yanında
            kaydın adı zaten metin olarak duruyor; okutulsaydı ekran okuyucu
            aynı şeyi iki kez söylerdi. */}
        {entry.coverImage ? (
          <motion.div className="chlog-shot" variants={shotV}>
            <img
              className="chlog-shot-img"
              src={entry.coverImage}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        ) : null}

        {entry.items.length > 0 ? (
          <ul className="chlog-changes">
            {entry.items.map((item, index) => (
              /* `key` olarak index: satırların kendi kimliği yok ve bu liste
                 istemcide hiç yeniden sıralanmıyor — sıra sunucudan geldiği
                 gibi kalıyor. */
              <motion.li
                className="chlog-change"
                data-kind={item.kind}
                key={index}
                variants={lineV}
              >
                {/* Etiket METİN, yalnız renk değil (DESIGN.md §1): renk
                    tarama hızını artırıyor, bilgiyi kelime taşıyor. */}
                <span className="chlog-kind">{t(`kinds.${item.kind}`)}</span>
                <span className="chlog-change-text">{item.text}</span>
              </motion.li>
            ))}
          </ul>
        ) : null}
      </article>
    </motion.li>
  );
}
